import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Checkbox,
  Button,
  Typography,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@material-ui/icons/Delete';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { RequirementTableSkeleton } from '../Skeletons';
import { REQUIREMENT_CATEGORIES, Requirement, displayStatus } from './types';
import { useSharedStyles } from './styles/shared';
import { useCategoryLabel } from './utils/useCategoryLabel';
import { formatDateTime } from '../../utils/formatDate';

/**
 * ASPICE score to show for a requirement: this session's analysis when there is
 * one, otherwise the score carried over from an earlier analysis.
 * Undefined means "never analysed" and renders as a dash — never as 0.
 */
function displayScore(req: Requirement): number | undefined {
  return req.analysis?.aspiceScore ?? req.aspiceScore;
}

/**
 * Category to filter a requirement by, taken from the same analysis as
 * displayScore. Undefined means "never analysed", which no category matches.
 */
function displayCategory(req: Requirement): string | undefined {
  return req.analysis?.category ?? req.category;
}

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  titleBox: {
    marginBottom: theme.spacing(2),
  },
  tableRow: {
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  reqId: {
    fontWeight: 600,
  },
  reqText: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipDefault: {},
  hintCell: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1),
    border: 'none',
  },
  approvedSelectedAlert: {
    marginBottom: theme.spacing(1),
  },
  actionRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  filterControl: {
    minWidth: 140,
  },
}));

type SortKey = 'id' | 'text' | 'status' | 'score';
type SortDir = 'asc' | 'desc';

interface RequirementTableProps {
  requirements: Requirement[];
  selectedIds: Set<string>;
  onSelectAll: (checked: boolean, displayedIds?: string[]) => void;
  onToggleSelect: (id: string) => void;
  onAnalyze: () => void;
  onDelete: () => void;
  onForceApprove?: () => void;
  /**
   * Runs redundancy detection. Scoped to the selection when
   * there is one, otherwise the whole project — hence no `disabled` on the
   * selection being empty.
   */
  onDetectRedundancies?: () => void;
  /**
   * Returns to the import screen. Required now that the page opens on the list
   * whenever the project already has requirements — without it the import
   * screen is unreachable.
   */
  onImportMore?: () => void;
  /** The role may analyze, import and detect redundancies (collaborator and above). */
  canWrite: boolean;
  /** The role may delete and force-approve (maintainer and above). */
  canApprove: boolean;
  loading?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const RequirementTable: React.FC<RequirementTableProps> = ({
  requirements,
  selectedIds,
  onSelectAll,
  onToggleSelect,
  onAnalyze,
  onDelete,
  onForceApprove,
  onDetectRedundancies,
  onImportMore,
  canWrite,
  canApprove,
  loading = false,
  error = null,
  onClearError,
}) => {
  // Approved threads are terminal: the backend refuses delete and force-approve
  // on them (409 thread-not-editable / thread-not-approvable). Rather than let the
  // batch fail halfway, block it and say which rows are in the way — the user
  // changes the selection and tries again.
  const selectedApproved = requirements.filter(
    r => selectedIds.has(r.id) && r.status === 'APPROVED',
  );
  const hasApprovedSelected = selectedApproved.length > 0;
  // A source locked in an open Merge proposal is refused the same way (409) until
  // the merge is accepted or rejected.
  const selectedMergeLocked = requirements.filter(
    r => selectedIds.has(r.id) && r.mergeLocked,
  );
  const hasMergeLockedSelected = selectedMergeLocked.length > 0;
  const batchBlocked = hasApprovedSelected || hasMergeLockedSelected;

  const { t } = useTranslation('requirement-doctor');
  const categoryLabel = useCategoryLabel();
  const shared = useSharedStyles();
  const classes = useStyles();

  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const displayed = useMemo(() => {
    let list = [...requirements];
    if (filterStatus)
      list = list.filter(r => displayStatus(r) === filterStatus);
    if (filterCategory)
      list = list.filter(r => displayCategory(r) === filterCategory);
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'id':
          cmp = a.id.localeCompare(b.id);
          break;
        case 'text':
          cmp = a.text.localeCompare(b.text);
          break;
        case 'status':
          cmp = displayStatus(a).localeCompare(displayStatus(b));
          break;
        case 'score':
          cmp = (displayScore(a) ?? -1) - (displayScore(b) ?? -1);
          break;
        default:
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [requirements, filterStatus, filterCategory, sortKey, sortDir]);

  // The taxonomy, then any other category the AI returned for a listed
  // requirement (so it is not unreachable), then "Unknown".
  const categoryOptions = useMemo(() => {
    const known = new Set<string>([...REQUIREMENT_CATEGORIES, 'Unknown']);
    const extra = new Set<string>();
    for (const r of requirements) {
      const category = displayCategory(r);
      if (category && !known.has(category)) extra.add(category);
    }
    // Kept while selected, so a filter whose last match was just fixed or
    // reloaded away stays visible and can be cleared.
    if (filterCategory && !known.has(filterCategory)) extra.add(filterCategory);
    return [...REQUIREMENT_CATEGORIES, ...[...extra].sort(), 'Unknown'];
  }, [requirements, filterCategory]);

  // Client-side pagination slice of the filtered+sorted list.
  const pagedDisplayed = useMemo(
    () => displayed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [displayed, page, rowsPerPage],
  );

  if (loading) {
    return <RequirementTableSkeleton />;
  }

  // The label follows the thread's lifecycle status, so what the row says is
  // what the API will accept. DRAFT used to render as "WIP", which is why the
  // screen offered actions that then failed.
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('s1.statuses.draft');
      case 'WIP':
        return t('s1.statuses.wip');
      case 'APPROVED':
        return t('s1.statuses.approved');
      case 'PENDING_SPLIT':
        return t('s1.statuses.pendingSplit');
      case 'PENDING_MERGE':
        return t('s1.statuses.pendingMerge');
      case 'SPLIT':
        return t('s1.statuses.split');
      case 'MERGED':
        return t('s1.statuses.merged');
      case 'DELETED':
        return t('s1.statuses.deleted');
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return classes.chipDefault;
      case 'WIP':
        return shared.chipInfo;
      case 'APPROVED':
        return shared.chipSuccess;
      // Both pending states are holds awaiting a decision elsewhere, so they
      // read as "needs attention" rather than as progress.
      case 'PENDING_SPLIT':
      case 'PENDING_MERGE':
        return shared.chipWarning;
      default:
        return classes.chipDefault;
    }
  };

  const getScoreColor = (score: number | undefined) => {
    if (score === undefined) return classes.chipDefault;
    if (score >= 7) return shared.chipSuccess;
    if (score >= 5) return shared.chipWarning;
    return shared.chipError;
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.titleBox}>
          <Typography variant="h6">{t('s1.title')}</Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            style={{ marginBottom: 12 }}
            onClose={onClearError}
          >
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box className={classes.filterRow}>
          <FormControl
            size="small"
            variant="outlined"
            className={classes.filterControl}
          >
            <InputLabel id="doctor-status-filter-label">
              {t('s1.table.columns.status')}
            </InputLabel>
            <Select
              labelId="doctor-status-filter-label"
              value={filterStatus}
              onChange={e => {
                setFilterStatus(e.target.value as string);
                setPage(0);
              }}
              label={t('s1.table.columns.status')}
            >
              <MenuItem value="">{t('s1.filter.all')}</MenuItem>
              <MenuItem value="DRAFT">{t('s1.statuses.draft')}</MenuItem>
              <MenuItem value="WIP">{t('s1.statuses.wip')}</MenuItem>
              <MenuItem value="APPROVED">{t('s1.statuses.approved')}</MenuItem>
              <MenuItem value="PENDING_SPLIT">
                {t('s1.statuses.pendingSplit')}
              </MenuItem>
              <MenuItem value="PENDING_MERGE">
                {t('s1.statuses.pendingMerge')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl
            size="small"
            variant="outlined"
            className={classes.filterControl}
          >
            <InputLabel id="doctor-category-filter-label">
              {t('common.category')}
            </InputLabel>
            <Select
              labelId="doctor-category-filter-label"
              value={filterCategory}
              onChange={e => {
                setFilterCategory(e.target.value as string);
                setPage(0);
              }}
              label={t('common.category')}
            >
              <MenuItem value="">{t('s1.filter.all')}</MenuItem>
              {categoryOptions.map(category => (
                <MenuItem key={category} value={category}>
                  {categoryLabel(category)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={shared.tableWrapper}>
          <Table size="small" style={{ tableLayout: 'fixed', minWidth: 800 }}>
            <TableHead className={shared.tableHead}>
              <TableRow>
                <TableCell
                  padding="none"
                  width="52px"
                  style={{ paddingLeft: 4 }}
                >
                  <Checkbox
                    checked={
                      displayed.length > 0 &&
                      displayed.every(r => selectedIds.has(r.id))
                    }
                    onChange={e =>
                      onSelectAll(
                        e.target.checked,
                        displayed.map(r => r.id),
                      )
                    }
                    title={t('s1.actions.selectAll')}
                  />
                </TableCell>
                <TableCell width="90px">
                  <TableSortLabel
                    active={sortKey === 'id'}
                    direction={sortKey === 'id' ? sortDir : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    {t('s1.table.columns.id')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width="280px">
                  <TableSortLabel
                    active={sortKey === 'text'}
                    direction={sortKey === 'text' ? sortDir : 'asc'}
                    onClick={() => handleSort('text')}
                  >
                    {t('s1.table.columns.text')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width="140px">
                  <TableSortLabel
                    active={sortKey === 'status'}
                    direction={sortKey === 'status' ? sortDir : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('s1.table.columns.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width="120px">
                  <TableSortLabel
                    active={sortKey === 'score'}
                    direction={sortKey === 'score' ? sortDir : 'asc'}
                    onClick={() => handleSort('score')}
                  >
                    {t('s1.table.columns.score')}
                  </TableSortLabel>
                </TableCell>
                {/* Wide enough for "YYYY-MM-DD HH:MM" on one line. */}
                <TableCell width="160px">
                  {t('s1.table.columns.updated')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requirements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {canWrite
                        ? t('s1.validation.noRequirements')
                        : t('s1.validation.noRequirementsReadOnly')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {displayed.length === 0 && requirements.length > 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {t('s1.filter.noMatch')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {displayed.length > 0 && selectedIds.size === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    className={classes.hintCell}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {t('s1.validation.selectAtLeast')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pagedDisplayed.map(req => (
                <TableRow key={req.id} hover className={classes.tableRow}>
                  <TableCell padding="none" style={{ paddingLeft: 4 }}>
                    <Checkbox
                      checked={selectedIds.has(req.id)}
                      onChange={() => onToggleSelect(req.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className={classes.reqId}>
                      {req.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      className={classes.reqText}
                      title={req.text}
                    >
                      {req.text}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(displayStatus(req))}
                      size="small"
                      className={getStatusColor(displayStatus(req))}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {displayScore(req) !== undefined ? (
                      <Chip
                        label={`${displayScore(req)!.toFixed(1)}`}
                        size="small"
                        className={getScoreColor(displayScore(req)!)}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {formatDateTime(req.updatedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={displayed.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={e => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </Box>

        {hasApprovedSelected && (
          <Alert severity="warning" className={classes.approvedSelectedAlert}>
            {t('s1.approvedSelectedWarning', {
              count: selectedApproved.length,
              ids: selectedApproved.map(r => r.id).join(', '),
            })}
          </Alert>
        )}
        {hasMergeLockedSelected && (
          <Alert severity="warning" className={classes.approvedSelectedAlert}>
            {t('s1.mergeLockedSelectedWarning', {
              count: selectedMergeLocked.length,
              ids: selectedMergeLocked.map(r => r.id).join(', '),
            })}
          </Alert>
        )}

        <Box className={classes.actionRow}>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={onAnalyze}
              disabled={selectedIds.size === 0}
            >
              {t('s1.actions.analyzeSelected')}
            </Button>
          )}
          {canApprove && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              disabled={selectedIds.size === 0 || batchBlocked}
            >
              {t('s1.actions.deleteSelected')}
            </Button>
          )}
          {canApprove && onForceApprove && (
            <Button
              variant="contained"
              onClick={onForceApprove}
              disabled={selectedIds.size === 0 || batchBlocked}
            >
              {t('s1.actions.forceApprove')}
            </Button>
          )}
          {canWrite && onImportMore && (
            <Button variant="outlined" onClick={onImportMore}>
              {t('s1.actions.importMore')}
            </Button>
          )}
          {canWrite && onDetectRedundancies && (
            <Button variant="outlined" onClick={onDetectRedundancies}>
              {selectedIds.size === 0
                ? t('s1.actions.detectRedundanciesAll')
                : t('s1.actions.detectRedundancies', {
                    count: selectedIds.size,
                  })}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RequirementTable;
